import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LockKeyhole, ShieldCheck, UserRound } from 'lucide-react';
import { login } from '../api/authApi';
import { setToken, setUser } from '../utils/auth';
import styles from './Login.module.scss';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async e => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    setError('');

    try {
      const response = await login(username, password);

      // Save token and user info
      setToken(response.access_token);
      setUser(response.user);

      const destination = location.state?.from?.pathname || '/';
      navigate(destination, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.detail;
        if (error.response.status === 401) {
          setError('Login failed, please check username and password');
        } else if (errorMessage) {
          // Show backend error message
          setError(errorMessage);
        } else {
          // Show status code if no detail message
          setError(`Server error: ${error.response.status} ${error.response.statusText}`);
        }
      } else if (error.request) {
        // Network error - request made but no response
        setError('Network error: Unable to connect to server');
      } else {
        // Other errors
        setError(`Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.brandStory}>
        <div className={styles.brandMark}>
          <img src="/images/logo.svg" alt="Terna Systems" />
        </div>
        <p className={styles.eyebrow}>Connected operations</p>
        <h1>Keep every machine in sight.</h1>
        <p className={styles.brandCopy}>
          Monitor your fleet, investigate incidents and safely deploy updates from one workspace.
        </p>
        <div className={styles.securityNote}>
          <ShieldCheck size={20} />
          <span>Secure access for Terna operations teams</span>
        </div>
      </div>

      <div className={styles.loginBox}>
        <div className={styles.loginHeader}>
          <span className={styles.mobileLogo}>
            <img src="/images/logo.svg" alt="Terna Systems" />
          </span>
          <p className={styles.eyebrow}>Terna Hub Cloud</p>
          <h2>Welcome back</h2>
          <span>Sign in to continue to the operations console.</span>
        </div>

        <form onSubmit={handleSubmit} className={styles.loginForm}>
          <div className={styles.formGroup}>
            <label htmlFor="username">Username</label>
            <div className={styles.inputControl}>
              <UserRound size={18} />
              <input
                type="text"
                id="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                autoComplete="username"
                disabled={loading}
                placeholder="Enter username"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password">Password</label>
            <div className={styles.inputControl}>
              <LockKeyhole size={18} />
              <input
                type="password"
                id="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={loading}
                placeholder="Enter password"
              />
            </div>
          </div>

          {error && <div className={styles.errorMessage}>{error}</div>}

          <button type="submit" className={styles.loginButton} disabled={loading}>
            {loading ? <span className={styles.buttonLoader}>Signing in…</span> : 'Sign in'}
          </button>
        </form>
        <p className={styles.loginFooter}>Terna Systems · Hub Cloud Operations</p>
      </div>
    </div>
  );
};

export default Login;
